import {Fragment, ReactElement, useState} from 'react'
import {Trans} from 'react-i18next'

import {Maybe} from '../../../../../graphql/types'
import messages from '../../../../../assets/messages'
import {WITHDRAWAL_METHODS_LIST, WithdrawalMethodValues} from '../constants'
import {WithdrawalMethodItem} from './WithdrawalMethodItem'
import {WithdrawalMethodModal} from './WithdrawalMethodModal'
import {useStyles} from './styles'

type ChooseWithdrawalMethodProps = {
  availableWithdrawalOptions: Maybe<string>[]
  isCardRefundSchemaPresented: boolean
  activeWithdrawalMethod: WithdrawalMethodValues | null
  remainingWithdrawalAmount: string | undefined
  bankTransferPaymentFields: Record<string, any> | null
  onBankTransferSet: (fieldValues) => void
  cryptoPaymentFields: Record<string, any> | null
  onCryptoSet: (fieldValues) => void
  cryptoPayoutPaymentFields: Record<string, any> | null
  onCryptoPayoutSet: (fieldValues) => void
  payRetailersPaymentFields: Record<string, any> | null
  onPayRetailersSet: (fieldValues) => void
  pixPayoutPaymentFields: Record<string, any> | null
  onPixPayoutSet: (fieldValues) => void
  onChange: (method: WithdrawalMethodValues) => void
}

function ChooseWithdrawalMethod(props: ChooseWithdrawalMethodProps): ReactElement {
  const classes = useStyles()
  const {
    availableWithdrawalOptions,
    isCardRefundSchemaPresented,
    activeWithdrawalMethod,
    remainingWithdrawalAmount,
    bankTransferPaymentFields,
    onBankTransferSet,
    cryptoPaymentFields,
    onCryptoSet,
    cryptoPayoutPaymentFields,
    onCryptoPayoutSet,
    payRetailersPaymentFields,
    onPayRetailersSet,
    pixPayoutPaymentFields,
    onPixPayoutSet,
    onChange,
  } = props
  const [addPayoutTypeModalOpen, setAddPayoutTypeModalOpen] = useState<
    WithdrawalMethodValues | false
  >(false)

  const handleAddPayoutModalOpen = (withdrawalMethod: WithdrawalMethodValues) =>
    setAddPayoutTypeModalOpen(withdrawalMethod)
  const handleAddPayoutModalClose = () => setAddPayoutTypeModalOpen(false)

  const methodsList = WITHDRAWAL_METHODS_LIST.filter(({value}) =>
    availableWithdrawalOptions.includes(value),
  )

  const withdrawalMethodPropsMap = {
    [WithdrawalMethodValues.bankTransfer]: bankTransferPaymentFields,
    [WithdrawalMethodValues.cryptocurrency]: cryptoPaymentFields,
    [WithdrawalMethodValues.enovipayCryptoPayout]: cryptoPayoutPaymentFields,
    [WithdrawalMethodValues.enovipayPixPayout]: pixPayoutPaymentFields,
    [WithdrawalMethodValues.payRetailers]: payRetailersPaymentFields,
  }

  return (
    <>
      <WithdrawalMethodModal
        addPayoutTypeModalOpen={addPayoutTypeModalOpen}
        bankTransferPaymentFields={bankTransferPaymentFields}
        onBankTransferSet={onBankTransferSet}
        cryptoPaymentFields={cryptoPaymentFields}
        onCryptoSet={onCryptoSet}
        cryptoPayoutPaymentFields={cryptoPayoutPaymentFields}
        onCryptoPayoutSet={onCryptoPayoutSet}
        payRetailersPaymentFields={payRetailersPaymentFields}
        onPayRetailersSet={onPayRetailersSet}
        pixPayoutPaymentFields={pixPayoutPaymentFields}
        onPixPayoutSet={onPixPayoutSet}
        onClose={handleAddPayoutModalClose}
      />
      <div className={classes.container}>
        <h3>
          {isCardRefundSchemaPresented ? (
            <Trans
              {...messages.chooseWithdrawalMethodForRemainingAmount}
              values={{amount: remainingWithdrawalAmount}}
              components={[
                <span className={classes.remainingWithdrawalAmount}>
                  remainingWithdrawalAmount
                </span>,
              ]}
            />
          ) : (
            <Trans {...messages.chooseWithdrawalMethod} />
          )}
        </h3>
        <div className={classes.list}>
          {methodsList.map(({value, title, processingTimeSubtitle, icon}, index) => (
            <Fragment key={value}>
              <WithdrawalMethodItem
                activeWithdrawalMethod={activeWithdrawalMethod}
                isEdit={Boolean(withdrawalMethodPropsMap[value])}
                value={value}
                icon={icon}
                processingTimeSubtitle={processingTimeSubtitle}
                title={title}
                onChange={onChange}
                onAddPayoutClick={handleAddPayoutModalOpen}
              />
              {index + 1 === methodsList.length ? null : <div className={classes.divider} />}
            </Fragment>
          ))}
        </div>
      </div>
    </>
  )
}

export {ChooseWithdrawalMethod}
